import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { getUsersWithFilters, setFilters, resetFilters } from '../redux/userSlice';
import { fetchUsersWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import { EyeIcon } from '@heroicons/react/16/solid';
import { addArtistOfMonth } from '../redux/ArtistOfMonthSlice';
import UserDetailModal from '../components/UserDetailModal';
import Notification from '../components/Notification';
import { toggleVerifiedStatus, toggleDeletedStatus } from '../services/api';
import RecordsLoader from '../components/RecordsLoader';
import ConfirmationDialog from '../components/ConfirmationDialog';

const UserList = () => {
    const dispatch = useDispatch();
    const { users, status, pagination, error } = useSelector((state) => state.users);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState({ verified: {}, deleted: {}, artistOfMonth: {} });

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(() => () => {});

    const [artistModalOpen, setArtistModalOpen] = useState(false);
    const [artistOfMonthData, setArtistOfMonthData] = useState({ userId: null, month: '', year: '' });

    const [notification, setNotification] = useState(null);

    const [localFilters, setLocalFilters] = useState({
        email: '',
        first_name: '',
        last_name: '',
        title: '',
        country: '',
        verified: '',
        deleted: '',
        subscribe_status: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'email',
            label: 'Email',
            type: 'text',
            placeholder: 'Enter email',
        },
        {
            key: 'first_name',
            label: 'First Name',
            type: 'text',
            placeholder: 'Enter first name',
        },
        {
            key: 'last_name',
            label: 'Last Name',
            type: 'text',
            placeholder: 'Enter last name',
        },
        {
            key: 'title',
            label: 'Title',
            type: 'text',
            placeholder: 'Enter title',
        },
        {
            key: 'country',
            label: 'Country',
            type: 'text',
            placeholder: 'Enter country',
        },
        {
            key: 'verified',
            label: 'Verified',
            type: 'select',
            options: [
                { value: '1', label: 'Verified' },
                { value: '0', label: 'Not Verified' },
            ],
        },
        {
            key: 'deleted',
            label: 'Status',
            type: 'select',
            options: [
                { value: '0', label: 'Active' },
                { value: '1', label: 'Deleted' },
            ],
        },
        {
            key: 'subscribe_status',
            label: 'Subscribe Status',
            type: 'select',
            options: [
                { value: 'subscribed', label: 'Subscribed' },
                { value: 'unsubscribed', label: 'Unsubscribed' },
            ],
        },
    ];

    // Fetch users with current filters and pagination
    const fetchUsers = useCallback(() => {
        const params = {
            user_type: 'user',
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getUsersWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleExport = async (format) => {
        try {
            const exportParams = {
                user_type: 'user',
                ...localFilters,
                export: format,
            };
            
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchUsersWithFilters(exportParams);
            handleExportResponse(response, format, `artists.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export artists. Please try again.');
        }
    };

    const handleRowClick = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const openConfirmationDialog = (message, action) => {
        setConfirmationMessage(message);
        setConfirmAction(() => action);
        setIsConfirmationOpen(true);
    };

    const closeConfirmationDialog = () => {
        setIsConfirmationOpen(false);
    };

    const openArtistOfMonthModal = (userId) => {
        setArtistOfMonthData({ userId, month: '', year: '' });
        setArtistModalOpen(true);
    };

    const closeArtistOfMonthModal = () => {
        setArtistModalOpen(false);
        setArtistOfMonthData({ userId: null, month: '', year: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setArtistOfMonthData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleAddArtistOfMonth = async () => {
        const { userId, month, year } = artistOfMonthData;

        if (!month || !year) {
            setNotification({
                message: 'Please enter both month and year.',
                type: 'error',
            });
            return;
        }

        setLoading((prev) => ({
            ...prev,
            artistOfMonth: { ...prev.artistOfMonth, [userId]: true }
        }));

        const user_id = userId;

        try {
            dispatch(addArtistOfMonth({ user_id, month, year }));
            setNotification({
                message: 'Artist added to the Artist of the Month table successfully.',
                type: 'success',
            });
            fetchUsers(); // Refresh the list
            closeArtistOfMonthModal();
        } catch (error) {
            console.error('Error adding artist to the Artist of the Month table:', error);
            setNotification({
                message: 'Failed to add the artist. Please try again.',
                type: 'error',
            });
        } finally {
            setLoading((prev) => ({
                ...prev,
                artistOfMonth: { ...prev.artistOfMonth, [userId]: false }
            }));
        }
    };

    const closeNotification = () => {
        setNotification(null);
    };

    const handleToggleVerified = (userId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus === 1 ? 'disapprove' : 'approve'} this artist?`,
            async () => {
                setLoading(prev => ({
                    ...prev,
                    verified: { ...prev.verified, [userId]: true }
                }));
                try {
                    await toggleVerifiedStatus(userId, 'user');
                    fetchUsers(); // Refresh the list
                } catch (error) {
                    console.error('Error toggling verified status:', error);
                } finally {
                    setLoading(prev => ({
                        ...prev,
                        verified: { ...prev.verified, [userId]: false }
                    }));
                    closeConfirmationDialog();
                }
            }
        );
    };

    const handleToggleDeleted = (userId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus === 1 ? 'activate' : 'deactivate'} this artist?`,
            async () => {
                setLoading(prev => ({
                    ...prev,
                    deleted: { ...prev.deleted, [userId]: true }
                }));
                try {
                    await toggleDeletedStatus(userId, 'user');
                    fetchUsers(); // Refresh the list
                } catch (error) {
                    console.error('Error toggling deleted status:', error);
                } finally {
                    setLoading(prev => ({
                        ...prev,
                        deleted: { ...prev.deleted, [userId]: false }
                    }));
                    closeConfirmationDialog();
                }
            }
        );
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading Artists, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Artists List</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Artists: {error || 'Unknown error'}
                </div>
            )}

            {/* Users Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={users || []}
                        getRowKey={(user) => user.id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'name',
                                label: 'Name',
                                render: (value, item) => `${item.first_name || ''} ${item.last_name || ''}`.trim() || '—',
                                flex: true,
                            },
                            {
                                key: 'email',
                                label: 'Email',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'title',
                                label: 'Title',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'verified',
                                label: 'Verified',
                                render: (value) => value === 1 ? 'Yes' : 'No',
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'country',
                                label: 'Country',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'deleted',
                                label: 'Active',
                                render: (value) => value === 1 ? 'Yes' : 'No',
                            },
                            {
                                key: 'subscribe_status',
                                label: 'Subscribe Status',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'profile_picture',
                                label: 'Profile Picture',
                                render: (value) => value ? (
                                    <img src={value} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                                ) : '—',
                            },
                            {
                                key: 'featured_image',
                                label: 'Featured Art',
                                render: (value) => value ? (
                                    <img src={value} alt="Art" className="w-32 h-16 rounded object-cover" />
                                ) : '—',
                            },
                        ]}
                        renderActions={(user, isExpanded) => (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRowClick(user);
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 hover:scale-110 border border-blue-200"
                                    title="View Details"
                                >
                                    <EyeIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleVerified(user.id, user.verified);
                                    }}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 border ${
                                        user.verified === 1
                                            ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                            : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                    }`}
                                    title={user.verified === 1 ? 'Disapprove' : 'Approve'}
                                    disabled={loading.verified[user.id]}
                                >
                                    {loading.verified[user.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {user.verified === 1 ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            )}
                                        </svg>
                                    )}
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleDeleted(user.id, user.deleted);
                                    }}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 border ${
                                        user.deleted === 1
                                            ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                            : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                    }`}
                                    title={user.deleted === 1 ? 'Activate' : 'Deactivate'}
                                    disabled={loading.deleted[user.id]}
                                >
                                    {loading.deleted[user.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {user.deleted === 1 ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            )}
                                        </svg>
                                    )}
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openArtistOfMonthModal(user.id);
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black hover:bg-gray-800 text-white transition-all duration-200 hover:scale-110 border border-gray-700"
                                    title="Add Artist of the Month"
                                    disabled={loading.artistOfMonth[user.id]}
                                >
                                    {loading.artistOfMonth[user.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </>
                        )}
                        emptyMessage="No artists found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}

            {/* User Detail Modal */}
            <UserDetailModal
                isOpen={isModalOpen}
                onClose={closeModal}
                user={selectedUser}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={isConfirmationOpen}
                message={confirmationMessage}
                onConfirm={confirmAction}
                onCancel={closeConfirmationDialog}
            />

            {/* Show Notification if it exists */}
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={closeNotification}
                />
            )}

            {/* Artist of the Month Modal */}
            {artistModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg w-[500px]">
                        <h2 className="text-lg font-bold mb-4">Add Artist of the Month</h2>

                        {/* Month Dropdown */}
                        <label className="block mb-2">
                            Month:
                            <select
                                name="month"
                                value={artistOfMonthData.month}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option>--Select Month--</option>
                                <option value="1">January</option>
                                <option value="2">February</option>
                                <option value="3">March</option>
                                <option value="4">April</option>
                                <option value="5">May</option>
                                <option value="6">June</option>
                                <option value="7">July</option>
                                <option value="8">August</option>
                                <option value="9">September</option>
                                <option value="10">October</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select>
                        </label>

                        {/* Year Dropdown */}
                        <label className="block mb-2">
                            Year:
                            <select
                                name="year"
                                value={artistOfMonthData.year}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option>--Select Year--</option>
                                <option value="2023">2023</option>
                                <option value="2024">2024</option>
                                <option value="2025">2025</option>
                                <option value="2026">2026</option>
                                <option value="2027">2027</option>
                            </select>
                        </label>

                        <div className="flex justify-end">
                            <button
                                onClick={handleAddArtistOfMonth}
                                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 mr-2"
                            >
                                {loading.artistOfMonth?.[artistOfMonthData?.userId] ? 'Adding...' : 'Add'}
                            </button>
                            <button
                                onClick={closeArtistOfMonthModal}
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserList;
